import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Manage Settings mega-menu (V34 parity) — the 3-dot menu's "Manage Settings"
 * entry. A multi-column popover with a search box and five sections. Items
 * route to existing surfaces where they exist and open a labelled shell where
 * the page is still being built (per-page phases). Ported 1:1 from
 * EHR_08-08-V34.html `settingsMenuHTML()`.
 */

/** What firing an item does. */
export type SettingsAction =
  | { kind: 'nav'; surface: string } // setActive(surface)
  | { kind: 'data'; section: string } // open DataSettingsModal at a rail section
  | { kind: 'config'; entity: string; title: string } // org-config list manager
  | { kind: 'actions' } // Status/Stage Actions engine
  | { kind: 'access-matrix' } // Access Rights Matrix
  | { kind: 'touch-rules' } // Touch Rules cadence templates
  | { kind: 'cpt-codes' } // calendar#38: the practice's CPT code list (the Calendar's chargemaster)
  | { kind: 'shell'; name: string; note?: string }; // labelled placeholder

type Item = {
  label: string;
  icon: string; // inner SVG markup
  action: SettingsAction;
  optional?: boolean;
  show?: boolean;
};
type Section = { label?: string; items: Item[] };

// ── Icons (inner markup; wrapped by <Svg/>) ──────────────────────────────────
const I = {
  // ehr#416: Auto logout
  lock: '<rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/>',
  users:
    '<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>',
  gear: '<circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>',
  matrix:
    '<rect x="3" y="3" width="18" height="18" rx="2"/><line x1="9" y1="3" x2="9" y2="21"/><line x1="3" y1="9" x2="21" y2="9"/>',
  access:
    '<line x1="6" y1="3" x2="6" y2="15"/><circle cx="18" cy="6" r="3"/><circle cx="6" cy="18" r="3"/><path d="M18 9a9 9 0 0 1-9 9"/>',
  loc: '<path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>',
  cal: '<rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>',
  tag: '<path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/>',
  layers:
    '<polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/>',
  contacts:
    '<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>',
  columns:
    '<rect x="3" y="3" width="7" height="18" rx="1"/><rect x="14" y="3" width="7" height="11" rx="1"/>',
  phone:
    '<path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>',
  bolt: '<polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>',
  task: '<path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>',
  colRules:
    '<rect x="3" y="3" width="18" height="18" rx="2"/><line x1="9" y1="3" x2="9" y2="21"/><line x1="15" y1="3" x2="15" y2="21"/>',
  template:
    '<path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22 6 12 13 2 6"/>',
  distribute:
    '<circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.6" y1="10.7" x2="15.4" y2="6.3"/><line x1="8.6" y1="13.3" x2="15.4" y2="17.7"/>',
  reroute:
    '<polyline points="16 3 21 3 21 8"/><line x1="4" y1="20" x2="21" y2="3"/><polyline points="21 16 21 21 16 21"/><line x1="15" y1="15" x2="21" y2="21"/><line x1="4" y1="4" x2="9" y2="9"/>',
  shield:
    '<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/>',
  // calendar#38: CPT codes
  cpt: '<path d="M4 6h16M4 12h16M4 18h10"/>',
  // ehr#309: Client Verification
  userCheck:
    '<path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><polyline points="17 11 19 13 23 9"/>',
  bucket:
    '<path d="M5 8h14l-1 11a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 8z"/><path d="M3 8h18"/><path d="M9 8V5a3 3 0 0 1 6 0v3"/>',
  overview:
    '<rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/><path d="M9 21V9"/>',
  reassign:
    '<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><polyline points="16 3 21 3 21 8"/><line x1="4" y1="20" x2="21" y2="3"/>',
  dollar:
    '<line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>',
  bars: '<line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>',
};

const Svg = ({ p }: { p: string }) => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
    dangerouslySetInnerHTML={{ __html: p }}
  />
);

/** ehr#649 (Chase): Data & settings opens on Practice info (it opened on Backup & data). */
export const DATA_SETTINGS_ACTION: SettingsAction = { kind: 'data', section: 'info' };

export function SettingsMegaMenu({
  onClose,
  onFire,
  canManage,
}: {
  onClose: () => void;
  onFire: (action: SettingsAction) => void;
  canManage: boolean;
}) {
  const [q, setQ] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    searchRef.current?.focus();
    const t = setTimeout(() => {
      const h = (e: MouseEvent) => {
        if (ref.current && !ref.current.contains(e.target as Node)) onClose();
      };
      document.addEventListener('mousedown', h);
      (ref.current as unknown as { _h?: (e: MouseEvent) => void })._h = h;
    }, 0);
    return () => {
      clearTimeout(t);
      const h = (ref.current as unknown as { _h?: (e: MouseEvent) => void })?._h;
      if (h) document.removeEventListener('mousedown', h);
    };
  }, [onClose]);
  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [onClose]);

  // Module-owned per OWNERSHIP.md — rendered 1:1 but as shells (no core backend).
  const MODULE = (name: string, module: string): SettingsAction => ({
    kind: 'shell',
    name,
    note: `${name} is owned by the ${module} module — not core. Building the shell here; full functionality lands with that module.`,
  });
  const SOON = (name: string): SettingsAction => ({
    kind: 'shell',
    name,
    note: `${name} — building in a later phase.`,
  });

  const sections: Section[] = useMemo(
    () => [
      {
        label: 'Manage users',
        items: [
          { label: 'Manage Users', icon: I.users, action: { kind: 'nav', surface: 'users' } },
          { label: 'Dashboard View', icon: I.gear, action: SOON('Dashboard View') },
          { label: 'Access Rights Matrix', icon: I.matrix, action: { kind: 'access-matrix' } },
          { label: 'Access Settings', icon: I.access, action: SOON('Access Settings') },
          // ehr#416: idle sign-out times (Data & settings > Auto logout)
          { label: 'Auto Logout', icon: I.lock, action: { kind: 'data', section: 'autologout' } },
          { label: 'Locations', icon: I.loc, action: { kind: 'data', section: 'locations' } },
          {
            label: 'Company Holidays',
            icon: I.cal,
            action: { kind: 'data', section: 'holidays' },
          },
        ],
      },
      {
        label: 'EHR configuration',
        items: [
          {
            label: 'Referral Sources',
            icon: I.tag,
            action: { kind: 'config', entity: 'referral-sources', title: 'Referral Sources' },
          },
          {
            label: 'Lead/Referral Types',
            icon: I.layers,
            action: SOON('Lead/Referral Types'),
            optional: true,
          },
          {
            label: 'Contact Types',
            icon: I.contacts,
            action: { kind: 'config', entity: 'contact-types', title: 'Contact Types' },
          },
          // calendar#34 — programs had an API but no screen.
          {
            label: 'Programs',
            icon: I.layers,
            action: { kind: 'config', entity: 'programs', title: 'Programs' },
          },
          {
            label: 'Stages',
            icon: I.columns,
            action: { kind: 'config', entity: 'stages', title: 'Stages' },
            optional: true,
          },
          {
            label: 'Stage Actions',
            icon: I.columns,
            action: { kind: 'actions' },
            optional: true,
          },
          {
            label: 'Statuses',
            icon: I.phone,
            action: { kind: 'config', entity: 'statuses', title: 'Statuses' },
          },
          { label: 'Status Actions', icon: I.bolt, action: { kind: 'actions' } },
          {
            label: 'Task Statuses',
            icon: I.phone,
            action: { kind: 'config', entity: 'task-statuses', title: 'Task Statuses' },
          },
          { label: 'Customize Tasks', icon: I.task, action: SOON('Customize Tasks') },
          { label: 'Column Rules', icon: I.colRules, action: SOON('Column Rules') },
          { label: 'Templates', icon: I.template, action: SOON('Templates') },
          // calendar#46 step 5 — NotePilot dictation switch.
          {
            label: 'Clinical Notes',
            icon: I.template,
            action: { kind: 'data', section: 'clinical' },
          },
          // reminders#4: every client's opt-outs (Data & settings → Opt-Out Center).
          {
            label: 'Opt-Out Center',
            icon: I.shield,
            action: { kind: 'data', section: 'optouts' },
          },
          // ehr#309: how clients prove who they are on link / QR forms (Data & settings).
          {
            label: 'Client Verification',
            icon: I.userCheck,
            action: { kind: 'data', section: 'verification' },
          },
        ],
      },
      {
        label: 'Routing & distribution',
        items: [
          {
            label: 'Distribution Rules',
            icon: I.distribute,
            action: MODULE('Distribution Rules', 'routing'),
            optional: true,
          },
          {
            label: 'Redistribution Rules',
            icon: I.reroute,
            action: MODULE('Redistribution Rules', 'routing'),
            optional: true,
          },
          { label: 'Touch Rules', icon: I.shield, action: { kind: 'touch-rules' }, optional: true },
          {
            label: 'Buckets',
            icon: I.bucket,
            action: MODULE('Buckets', 'routing'),
            optional: true,
          },
          {
            label: 'Bucket Overview',
            icon: I.overview,
            action: MODULE('Bucket Overview', 'routing'),
            optional: true,
          },
          // ehr#730 (V11 decision 4a): the Share & Reassign wizard is retired; Share and Reassign are on
          // each chart's ⋮ menu and on the Contacts list's bulk bar.
          {
            label: 'Automations',
            icon: I.bolt,
            action: MODULE('Automations', 'reminders'),
            optional: true,
          },
        ],
      },
      {
        label: 'Money & insights',
        items: [
          { label: 'Invoicing', icon: I.dollar, action: { kind: 'nav', surface: 'billing' } },
          { label: 'Reporting', icon: I.bars, action: { kind: 'nav', surface: 'reports' } },
          // calendar#38 (Chase): between Reporting and Data & settings.
          { label: 'CPT codes', icon: I.cpt, action: { kind: 'cpt-codes' } },
        ],
      },
      {
        items: [{ label: 'Data & settings', icon: I.gear, action: DATA_SETTINGS_ACTION }],
      },
    ],
    [canManage],
  );

  const ql = q.trim().toLowerCase();
  const match = (label: string) => !ql || label.toLowerCase().includes(ql);

  const body = (
    <div className="settings-mega-backdrop">
      <div
        className="settings-mega"
        ref={ref}
        role="menu"
        aria-label="Manage Settings"
        style={{ columnCount: ql ? 1 : undefined }}
      >
        <div className="settings-mega-search-wrap">
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            ref={searchRef}
            type="search"
            placeholder="Search settings…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            autoComplete="off"
          />
        </div>
        {sections.map((sec, si) => {
          const items = sec.items.filter((it) => it.show !== false && match(it.label));
          if (!items.length) return null;
          return (
            <div className="settings-mega-sec" key={sec.label ?? `sec-${si}`}>
              {sec.label && <div className="settings-mega-label">{sec.label}</div>}
              {items.map((it) => (
                <button
                  key={it.label}
                  type="button"
                  className={`settings-mega-item${it.optional ? ' optional' : ''}`}
                  onClick={() => {
                    onClose();
                    onFire(it.action);
                  }}
                >
                  <Svg p={it.icon} />
                  <span className="smi-label">{it.label}</span>
                  {it.optional && <span className="smi-optional-pill">Optional</span>}
                </button>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
  return createPortal(body, document.body);
}
